/**
 * Dashboard Data Service
 * Phase 9.3: Unified data hub for Self Dashboard
 * 
 * NOW USES VAULT SERVICE for unified data access across ALL content sources.
 * This ensures Smart Insights, Memory Surfacing, Weekly Summary, etc. all see the same data.
 */

import { supabase } from '../lib/supabase';
import smartSwirlsService from './smartSwirlsService';
import { getReflectionsForPeriod } from './reflectionService';
import vaultService from './vaultService';

const CACHE_KEY_PREFIX = 'dashboard_data_cache';
const CACHE_EXPIRY_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Get cached dashboard data
 */
function getCachedData(userId) {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}_${userId}`);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > CACHE_EXPIRY_MS) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}_${userId}`);
      return null;
    }

    const hoursRemaining = (CACHE_EXPIRY_MS - age) / (60 * 60 * 1000);
    if (import.meta.env.DEV) {
      console.log(`⚙️ Using cached dashboard data (${hoursRemaining.toFixed(1)}h remaining)`);
    }
    return data;
  } catch (error) {
    console.error('Error reading dashboard cache:', error);
    return null;
  }
}

/**
 * Cache dashboard data
 */
function setCachedData(userId, data) {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(`${CACHE_KEY_PREFIX}_${userId}`, JSON.stringify(cacheData));
    if (import.meta.env.DEV) {
      console.log('💾 Cached dashboard data for 6 hours');
    }
  } catch (error) {
    console.error('Error caching dashboard data:', error);
  }
}

/**
 * Fetch search queries for analytics
 */
async function getQueries(userId, limit = 10) {
  console.log('🔍 [DEBUG] getQueries called:', { userId: userId?.slice(0, 8), limit });
  
  try {
    const { data, error } = await supabase
      .schema('deepsearch')
      .from('queries')
      .select('query, created_at, results_count')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      // Handle missing table gracefully - silently return empty
      if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.code === 'PGRST301' || error.message?.includes('does not exist') || error.message?.includes('schema cache')) {
        // Silently return empty - this is expected if deepsearch schema not exposed
        return [];
      }
      // Only log unexpected errors
      if (import.meta.env.DEV) {
        console.warn('⚠️ Unexpected queries error:', { code: error.code, message: error.message });
      }
      return [];
    }

    console.log('✅ [DEBUG] getQueries success:', {
      rowCount: data?.length || 0,
      sample: data?.[0] || null,
    });

    return data || [];
  } catch (error) {
    console.error('❌ [DEBUG] Error fetching queries:', error);
    return [];
  }
}

/**
 * Get curiosity memory data (Phase 9.7.2)
 * @param {string} userId - User ID
 * @param {number} weeks - Number of weeks to fetch (default: 12)
 * @returns {Promise<Array>} Array of curiosity memory records
 */
async function getCuriosityMemory(userId, weeks = 12) {
  console.log('🔍 [DEBUG] getCuriosityMemory called:', { userId: userId?.slice(0, 8), weeks });
  
  try {
    const { data, error } = await supabase
      .schema('deepsearch')
      .from('user_curiosity_memory')
      .select('*')
      .eq('user_id', userId)
      .order('week_start', { ascending: false })
      .limit(weeks);

    if (error) {
      // Handle missing table gracefully - silently return empty array
      if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.code === 'PGRST301' || error.message?.includes('does not exist') || error.message?.includes('schema cache')) {
        // Silently return empty - this is expected if deepsearch schema not exposed
        return [];
      }
      // Only log unexpected errors
      if (import.meta.env.DEV) {
        console.warn('⚠️ Unexpected curiosity memory error:', { code: error.code, message: error.message });
      }
      return [];
    }

    console.log('✅ [DEBUG] Curiosity memory query success:', {
      rowCount: data?.length || 0,
      sample: data?.[0] || null,
      allData: data,
    });

    return data || [];
  } catch (error) {
    console.error('❌ [DEBUG] Error fetching curiosity memory:', error);
    return [];
  }
}

/**
 * Load all dashboard data from unified service
 * @param {string} userId - User ID
 * @param {boolean} useCache - Whether to use cached data (default: true)
 * @returns {Promise<Object>} Unified data object with items, reflections, swirls, queries, memory
 */
export async function loadDashboardData(userId, useCache = true) {
  if (!userId) {
    return {
      items: [],
      reflections: [],
      swirls: [],
      queries: [],
      memory: [],
    };
  }

  // Check cache first (if enabled)
  if (useCache) {
    const cached = getCachedData(userId);
    if (cached) {
      return cached;
    }
  }

  // Fetch all data sources in parallel using Promise.allSettled
  // This ensures one failure doesn't block others
  const dateRange = getDateRange(4); // Last 4 weeks
  console.log('🔍 [DEBUG] loadDashboardData - Starting parallel fetches:', {
    userId: userId?.slice(0, 8),
    dateRange: {
      start: dateRange.startDate.toISOString(),
      end: dateRange.endDate.toISOString(),
    },
  });

  // Fetch items from UNIFIED VAULT (all sources), plus swirls and queries
  const [itemsResult, swirlsResult, queriesResult] = await Promise.allSettled([
    vaultService.getAllItems(userId, { forceRefresh: !useCache }), // ✅ UNIFIED VAULT - all content sources
    smartSwirlsService.getSmartSwirlInsights(userId),
    getQueries(userId, 20),
  ]);

  console.log('🔍 [DEBUG] loadDashboardData - Promise.allSettled results:', {
    itemsResult: {
      status: itemsResult.status,
      hasData: itemsResult.status === 'fulfilled' && itemsResult.value !== undefined,
      dataLength: itemsResult.status === 'fulfilled' && Array.isArray(itemsResult.value) ? itemsResult.value.length : 'N/A',
      error: itemsResult.status === 'rejected' ? itemsResult.reason : null,
    },
    swirlsResult: {
      status: swirlsResult.status,
      hasData: swirlsResult.status === 'fulfilled' && swirlsResult.value !== undefined,
      dataLength: swirlsResult.status === 'fulfilled' && Array.isArray(swirlsResult.value) ? swirlsResult.value.length : 'N/A',
      error: swirlsResult.status === 'rejected' ? swirlsResult.reason : null,
    },
    queriesResult: {
      status: queriesResult.status,
      hasData: queriesResult.status === 'fulfilled' && queriesResult.value !== undefined,
      dataLength: queriesResult.status === 'fulfilled' && Array.isArray(queriesResult.value) ? queriesResult.value.length : 'N/A',
      error: queriesResult.status === 'rejected' ? queriesResult.reason : null,
    },
  });

  // Helper to unwrap Promise.allSettled results
  const unwrap = (result) => {
    if (result.status === 'fulfilled') {
      // Handle different return formats
      if (result.value && typeof result.value === 'object') {
        // smartSwirlsService.getSmartSwirlInsights returns array directly
        if (Array.isArray(result.value)) {
          return result.value;
        }
        // smartSwirlsService.getSmartSwirls returns { data, error }
        if ('data' in result.value && 'error' in result.value) {
          return result.value.error ? [] : (result.value.data || []);
        }
        // reflectionService returns { data, error }
        if ('data' in result.value && 'error' in result.value) {
          return result.value.error ? [] : (result.value.data || []);
        }
        // Array or other object
        return Array.isArray(result.value) ? result.value : [];
      }
      return Array.isArray(result.value) ? result.value : [];
    }
    console.warn('Dashboard data fetch failed:', result.reason);
    return [];
  };

  // Items come from UNIFIED VAULT - already normalized
  // Map vault schema back to dashboard expected format for backward compatibility
  const vaultItems = unwrap(itemsResult);
  const enrichedItems = vaultItems.map(item => {
    // Vault items use camelCase, dashboard expects snake_case
    let thumbnail_url = item.thumbnailUrl || item.imageUrl || item.url;
    
    // YouTube thumbnail extraction
    if (!thumbnail_url && item.url) {
      const youtubeMatch = item.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
      if (youtubeMatch) {
        thumbnail_url = `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`;
      }
    }
    
    // Final fallback
    if (!thumbnail_url) {
      thumbnail_url = '/img/placeholders/card.png';
    }
    
    return {
      // Core fields (snake_case for backward compatibility)
      id: item.id,
      user_id: item.userId,
      created_at: item.createdAt,
      updated_at: item.updatedAt,
      title: item.title,
      description: item.description,
      url: item.url,
      thumbnail_url,
      source_type: item.sourceType || 'link',
      channel: item.channel,
      // Analysis fields
      ai_analysis: item.aiAnalysis || {},
      analysis_data: item.analysisData || {},
      // Extracted fields
      summary: item.aiAnalysis?.summary || item.analysisData?.summary || item.description || '',
      bullet_points: item.aiAnalysis?.bullet_points || item.analysisData?.bullet_points || [],
      tags: item.tags || [],
      auto_tags: item.autoTags || {},
      themes: item.extractedThemes || [],
      // Vault traceability
      _vaultSource: item.originalSourceTable,
      _vaultId: item.originalSourceId,
    };
  });

  const unifiedData = {
    items: enrichedItems,
    reflections: [], // Disabled - table doesn't exist
    swirls: unwrap(swirlsResult),
    queries: queriesResult.status === 'fulfilled' ? (queriesResult.value || []) : [],
    memory: [], // Disabled - table doesn't exist (Phase 9.7.2)
  };

  console.log('📊 [DEBUG] loadDashboardData - Unified data summary:', {
    items: {
      length: unifiedData.items.length,
      isEmpty: unifiedData.items.length === 0,
      sample: unifiedData.items[0] || null,
    },
    reflections: {
      length: unifiedData.reflections.length,
      isEmpty: unifiedData.reflections.length === 0,
      sample: unifiedData.reflections[0] || null,
    },
    swirls: {
      length: unifiedData.swirls.length,
      isEmpty: unifiedData.swirls.length === 0,
      sample: unifiedData.swirls[0] || null,
    },
    queries: {
      length: unifiedData.queries.length,
      isEmpty: unifiedData.queries.length === 0,
      sample: unifiedData.queries[0] || null,
    },
    memory: {
      length: unifiedData.memory.length,
      isEmpty: unifiedData.memory.length === 0,
      sample: unifiedData.memory[0] || null,
      allRecords: unifiedData.memory,
    },
  });

  // Check for empty data and log which table queries might have failed
  if (unifiedData.items.length === 0) {
    console.warn('⚠️ [DEBUG] Items array is empty - check deepsearch.items table query');
  }
  if (unifiedData.reflections.length === 0) {
    console.warn('⚠️ [DEBUG] Reflections array is empty - check deepsearch.reflections table query');
  }
  if (unifiedData.swirls.length === 0) {
    console.warn('⚠️ [DEBUG] Swirls array is empty - check deepsearch.smart_swirls table query');
  }
  if (unifiedData.queries.length === 0) {
    console.warn('⚠️ [DEBUG] Queries array is empty - check deepsearch.queries table query');
  }
  if (unifiedData.memory.length === 0) {
    console.warn('⚠️ [DEBUG] Memory array is empty - check deepsearch.user_curiosity_memory table query or migration status');
  }

  // Cache the result
  if (useCache) {
    setCachedData(userId, unifiedData);
  }

  return unifiedData;
}

/**
 * Get date range for reflections query
 * @param {number} weeks - Number of weeks to look back
 * @returns {Object} { startDate: Date, endDate: Date }
 */
function getDateRange(weeks) {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - (weeks * 7));
  return { startDate, endDate };
}

/**
 * Clear dashboard data cache
 * @param {string} userId - User ID
 */
export function clearDashboardCache(userId) {
  try {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}_${userId}`);
    if (import.meta.env.DEV) {
      console.log('🗑️ Cleared dashboard data cache');
    }
  } catch (error) {
    console.error('Error clearing dashboard cache:', error);
  }
}

